import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaBookReader,
  FaBookOpen,
  FaSearch,
  FaCheckCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import SubjectForm from "../../Components/forms/SubjectForm";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function SubjectList() {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { token } = useAuth();

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/subjects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(res.data.data || []);
    } catch (error) {
      toast.error("Failed to load subjects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchSubjects();
  }, [token]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this subject?")) {
      try {
        await axios.delete(`${server}/subjects/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Subject deleted successfully.");
        fetchSubjects();
      } catch (error) {
        toast.error("Error deleting subject");
      }
    }
  };

  const openEditModal = (subject) => {
    setSelectedSubject(subject);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setSelectedSubject(null);
    setIsModalOpen(true);
  };

  const filteredSubjects = subjects.filter(
    (s) =>
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaBookOpen size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Curriculum Subjects Master</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Define and manage academic subjects offered across all classes</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={openCreateModal}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Add New Subject
          </button>
        </div>
      </div>

      {/* KPI Stat Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Total Subjects</span>
          <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-0.5">{subjects.length}</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Active Curriculum Items</span>
          <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
            {subjects.filter((s) => s.isActive).length}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs col-span-2 sm:col-span-1">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Search Filter</span>
          <div className="text-xs font-bold text-gray-700 dark:text-slate-300 mt-1 truncate">
            {searchTerm || "Showing All"}
          </div>
        </div>
      </div>

      {/* Search Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="text-xs font-black text-gray-700 dark:text-slate-300">
          Subjects Directory ({filteredSubjects.length})
        </div>

        <div className="relative w-full sm:w-64">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            placeholder="Search Subject Name or Code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
          />
        </div>
      </div>

      {/* High-Density Table Display */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase border-b border-gray-200 dark:border-slate-800">
                <th className="p-2.5">Subject Code</th>
                <th className="p-2.5">Subject Name</th>
                <th className="p-2.5">Description</th>
                <th className="p-2.5">Status</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-indigo-600 font-bold">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Loading curriculum subjects...
                  </td>
                </tr>
              ) : filteredSubjects.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-gray-400 italic">
                    No matching subject records found.
                  </td>
                </tr>
              ) : (
                filteredSubjects.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition">
                    <td className="p-2.5 font-mono font-black text-indigo-600 dark:text-indigo-400">{sub.code}</td>
                    <td className="p-2.5 font-black text-gray-900 dark:text-white">{sub.name}</td>
                    <td className="p-2.5 text-gray-500 dark:text-slate-400 font-medium">{sub.description || "N/A"}</td>
                    <td className="p-2.5 font-bold">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                        sub.isActive !== false
                          ? "bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400"
                          : "bg-gray-100 dark:bg-slate-800 text-gray-500"
                      }`}>
                        {sub.isActive !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => openEditModal(sub)}
                          className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 rounded-lg hover:bg-indigo-100 transition"
                          title="Edit Subject"
                        >
                          <FaEdit size={11} />
                        </button>
                        <button
                          onClick={() => handleDelete(sub.id)}
                          className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-lg hover:bg-rose-100 transition"
                          title="Delete Subject"
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Component */}
      <SubjectForm
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        subject={selectedSubject}
        subjectToEdit={selectedSubject}
        onSuccess={fetchSubjects}
      />
    </div>
  );
}
